import { Link } from 'react-router-dom';
import { useEntity } from '@backstage/plugin-catalog-react';
import { makeStyles } from 'tss-react/mui';
import {
  DocumentIcon,
  getColors,
  typography,
} from '@mercedes-benz/odp-theme-v2';

const useStyles = makeStyles()(theme => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
  return {
    root: {
      background: c.background.paper,
      borderRadius: 12,
      border: `1px solid ${c.border.default}`,
      padding: 48,
      minHeight: 400,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      textAlign: 'center',
    },
    iconWrapper: {
      width: 56,
      height: 56,
      borderRadius: '50%',
      background: 'rgba(0, 120, 214, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& svg': {
        width: 28,
        height: 28,
        color: c.primary.main,
      },
    },
    title: {
      fontFamily: typography.fontFamilyCorpoSText,
      color: c.text.primary,
      fontWeight: 600,
      fontSize: '1.1rem',
      margin: 0,
      userSelect: 'none',
    },
    description: {
      fontFamily: typography.fontFamilyCorpoSText,
      color: fgAlpha(0.55),
      fontSize: '0.875rem',
      margin: 0,
      maxWidth: 400,
      lineHeight: 1.6,
    },
    link: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.875rem',
      fontWeight: 500,
      color: c.text.link,
      textDecoration: 'none',
      padding: '8px 20px',
      borderRadius: 20,
      border: `1px solid ${c.primary.main}`,
      transition: 'background 0.15s ease',
      '&:hover': {
        background: 'rgba(0, 120, 214, 0.08)',
        textDecoration: 'none',
      },
    },
  };
});

export const DocsTab = () => {
  const { classes } = useStyles();
  const { entity } = useEntity();

  const namespace = entity.metadata.namespace ?? 'default';
  const kind = entity.kind.toLowerCase();
  const name = entity.metadata.name;
  const docsPath = `/docs/${namespace}/${kind}/${name}`;

  const hasDocs =
    entity.metadata.annotations?.['backstage.io/techdocs-ref'] !== undefined;

  if (!hasDocs) {
    return (
      <div className={classes.root}>
        <div className={classes.iconWrapper}>
          <DocumentIcon />
        </div>
        <h3 className={classes.title}>No documentation available</h3>
        <p className={classes.description}>
          This component has not been set up with TechDocs yet. Add a{' '}
          <code>backstage.io/techdocs-ref</code> annotation to enable
          documentation.
        </p>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.iconWrapper}>
        <DocumentIcon />
      </div>
      <h3 className={classes.title}>Documentation available</h3>
      <p className={classes.description}>
        Open the full documentation for this component in the docs reader.
      </p>
      <Link to={docsPath} className={classes.link}>
        <DocumentIcon />
        Open Docs
      </Link>
    </div>
  );
};
